import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OpsThemeProvider, T } from "@/components/admin/ui";
import { ADMIN_BASE } from "@/lib/adminPath";

function Form() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/staff-login", form);
      localStorage.setItem("nv_token", data.access_token);
      setUser(data.user);
      navigate(ADMIN_BASE, { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${T.page} relative flex items-center justify-center px-4 py-16 overflow-hidden`}>
      <div
        className="absolute inset-0 pointer-events-none opacity-60 dark:opacity-30"
        style={{
          backgroundImage: "radial-gradient(circle, rgb(148 163 184 / 0.35) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />
      <div className="relative w-full max-w-[400px]">
        <div className="flex items-center gap-2.5 mb-7">
          <div className="w-9 h-9 rounded-lg bg-emerald-600 dark:bg-emerald-500 grid place-items-center shadow-sm">
            <ShieldCheck className="w-4.5 h-4.5 text-white dark:text-slate-950" />
          </div>
          <div className="leading-tight">
            <p className="text-[15px] font-semibold tracking-tight">Propojo</p>
            <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400 font-medium">
              Ops console
            </p>
          </div>
        </div>

        <div className={`${T.card} p-6`}>
          <h1 className="text-lg font-semibold tracking-tight">Staff sign in</h1>
          <p className={`text-[13px] mt-1.5 ${T.muted}`}>Restricted to Propojo platform staff. Every sign-in is logged.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="ops-signin-form">
            <div>
              <label className={T.label} htmlFor="ops-email">
                Work email
              </label>
              <input
                id="ops-email"
                data-testid="ops-email-input"
                type="email"
                required
                autoComplete="username"
                value={form.email}
                onChange={set("email")}
                className={`${T.input} w-full mt-1.5`}
                placeholder="you@propojo.com"
              />
            </div>
            <div>
              <label className={T.label} htmlFor="ops-password">
                Password
              </label>
              <input
                id="ops-password"
                data-testid="ops-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={form.password}
                onChange={set("password")}
                className={`${T.input} w-full mt-1.5`}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p
                data-testid="ops-signin-error"
                className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-md px-3 py-2"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="ops-signin-submit"
              disabled={busy}
              className={`${T.btn} ${T.btnPrimary} w-full py-2.5 text-sm`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
              {busy ? "Verifying" : "Sign in"}
            </button>
          </form>
        </div>

        <p className={`text-xs mt-5 text-center ${T.muted}`}>
          Not staff?{" "}
          <a href="/login" data-testid="ops-signin-customer-link" className="text-emerald-700 dark:text-emerald-400 hover:underline">
            Customer sign in
          </a>
        </p>
      </div>
    </div>
  );
}

export default function OpsSignin() {
  return (
    <OpsThemeProvider>
      <Form />
    </OpsThemeProvider>
  );
}
